import * as DateF from 'date-fns'
import styled from 'styled-components'

import { Eye1Icon } from '@blade/ui-kit'
import { PremiumText } from '@root/entities/premium/ui'
import { DATE_FORMAT } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { Avatar } from '@root/shared/ui-kit'

import type { PremiumId, PremiumOwner } from '../../model'

export type PremiumArchivedListProps = {
  onClick: (premiumId: PremiumId) => void
  items: Array<ItemProps>
  current: CurrentItemProps | undefined
}

type CurrentItemProps = {
  id: PremiumId
  owner: PremiumOwner | undefined
}
type ItemProps = {
  id: PremiumId
  owner: PremiumOwner | undefined
  archivedAt: Date
  isSelected: boolean
}

export const PremiumArchivedList = (props: PremiumArchivedListProps) => {
  const { t } = useTranslation()

  const renderCurrentItem = () => {
    const { current } = props
    if (!current) {
      return undefined
    }
    const { username, avatarUrl } = current.owner ?? {}
    return (
      <ListItem
        data-current
        type="button"
        onClick={() => props.onClick(current.id)}
      >
        <Owner>
          {username ? (
            <Avatar size={16} src={avatarUrl} fallback={username.at(0)} />
          ) : undefined}
          {username || '—'}
        </Owner>
        <ArchivedAt>
          <Premium>{t('premium.archived-list.current').toLowerCase()}</Premium>
        </ArchivedAt>
      </ListItem>
    )
  }

  return (
    <Root>
      <Header>
        <Premium>{t('common.d-premium')}</Premium>{' '}
        {t('premium.archived-list.title').toLowerCase()}
      </Header>
      <ListHeader>
        <div>{t('premium.archived-list.owner').toLowerCase()}</div>
        <div>{t('premium.archived-list.date').toLowerCase()}</div>
      </ListHeader>
      <ListBody>
        {renderCurrentItem()}
        {props.items.map((item) => {
          const { username, avatarUrl } = item.owner ?? {}
          return (
            <ListItem
              onClick={() => props.onClick(item.id)}
              type="button"
              key={item.id}
              data-selected={item.isSelected}
            >
              <Owner>
                {username ? (
                  <Avatar size={16} src={avatarUrl} fallback={username.at(0)} />
                ) : undefined}
                {username || '—'}
              </Owner>
              {item.isSelected ? <SelectedIcon /> : undefined}
              <ArchivedAt>
                {DateF.format(item.archivedAt, DATE_FORMAT.dd_MM_yy)}
              </ArchivedAt>
            </ListItem>
          )
        })}
      </ListBody>
    </Root>
  )
}

const Premium = styled(PremiumText)`
  font-size: 11px;
  font-weight: 600;
`

const SelectedIcon = styled(Eye1Icon)`
  margin-left: auto;
`

const Root = styled.div`
  min-width: 230px;
  overflow: hidden;
  border-radius: 6px;
  border: 1px solid var(--background-4);
  background: var(--appearanceA-1);
  box-shadow: 0 6px 32px 0 var(--appearanceA-4);
  backdrop-filter: blur(120px);
  color: var(--textA-10);
`
const Header = styled.div`
  padding: 7px 10px;
  color: var(--textA-11);
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
`
const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  background: var(--backgroundA-2);
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  padding: 4px 10px;
`

const ListBody = styled.div`
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
`
const ArchivedAt = styled.span``
const Owner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`
const ListItem = styled.button`
  all: unset;
  box-sizing: border-box;
  width: 100%;
  border-bottom: 1px solid var(--backgroundA-2);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  padding: 7px 10px;

  &:hover,
  &[data-selected='true'] {
    color: var(--textA-12);
    background: var(--backgroundA-2);
  }

  &[data-current] {
    color: var(--textA-12);
    border-bottom-color: var(--subscriptionGradientTo-8);
    background:
      radial-gradient(
        61.67% 25.52% at 70.83% 111.67%,
        var(--subscriptionGradientToA-5) 0%,
        transparent 100%
      ),
      radial-gradient(
        123.68% 138.36% at 76.54% 156.67%,
        var(--subscriptionGradientFromA-7) 0%,
        transparent 100%
      ),
      radial-gradient(
        92.65% 40.39% at 86.84% 111.67%,
        var(--subscriptionGradientToA-5) 0%,
        transparent 100%
      );
    background-size: 100% calc(100% + 10px);
    background-position:
      0 -10px,
      0 -10px,
      0 -10px;
    transition: background-position 100ms ease;

    &:hover {
      background-position:
        0 0,
        0 0,
        0 0;
    }
  }

  &:last-child {
    border-bottom: none;
  }
`
